import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import React, { ReactNode, useState } from "react";
import {
  LayoutAnimation,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  UIManager,
  View,
  ViewStyle,
} from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import ThemedText from "./ThemedText";

// Enable LayoutAnimation on Android
if (
  Platform.OS === "android" &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

interface AccordionItemProps {
  title: string;
  children: ReactNode;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  icon?: ReactNode;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function AccordionItem({
  title,
  children,
  expanded = false,
  onToggle,
  icon,
  disabled = false,
  style,
}: AccordionItemProps) {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const backgroundColor = useThemeColor({}, "backgroundSecondary");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const iconRotation = useSharedValue(expanded ? 180 : 0);

  const handleToggle = () => {
    if (disabled) return;

    const newExpanded = !isExpanded;

    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsExpanded(newExpanded);

    iconRotation.value = withTiming(newExpanded ? 180 : 0, { duration: 200 });

    onToggle?.(newExpanded);
  };

  const animatedIconStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${iconRotation.value}deg` }],
  }));

  return (
    <View
      style={[
        styles.accordionItem,
        { backgroundColor, borderColor },
        disabled && styles.disabled,
        style,
      ]}
    >
      <TouchableOpacity
        style={styles.header}
        onPress={handleToggle}
        disabled={disabled}
        activeOpacity={0.7}
      >
        {icon && <View style={styles.iconContainer}>{icon}</View>}

        <ThemedText style={styles.title}>{title}</ThemedText>

        <Animated.View style={animatedIconStyle}>
          <Ionicons name="chevron-down" size={20} color={textColor} />
        </Animated.View>
      </TouchableOpacity>

      {isExpanded && (
        <View style={[styles.content, { borderTopColor: borderColor }]}>
          {children}
        </View>
      )}
    </View>
  );
}

interface AccordionProps {
  children: ReactNode;
  allowMultiple?: boolean;
  style?: StyleProp<ViewStyle>;
}

export default function Accordion({
  children,
  allowMultiple = false,
  style,
}: AccordionProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const handleItemToggle = (index: number, expanded: boolean) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (expanded) {
        if (!allowMultiple) {
          next.clear();
        }
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
  };

  return (
    <View style={[styles.accordion, style]}>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child) && child.type === AccordionItem) {
          const typedChild = child as React.ReactElement<AccordionItemProps>;
          return React.cloneElement(typedChild, {
            expanded: expandedItems.has(index),
            onToggle: (expanded: boolean) => {
              handleItemToggle(index, expanded);
              typedChild.props.onToggle?.(expanded);
            },
          });
        }
        return child;
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  accordion: {
    gap: 8,
  },
  accordionItem: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    minHeight: 56,
  },
  iconContainer: {
    marginRight: 12,
  },
  title: {
    flex: 1,
    fontSize: 16,
    fontFamily: fonts.semiBold,
  },
  content: {
    padding: 16,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  disabled: {
    opacity: 0.5,
  },
});
